import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { CountryLabel } from "@/components/CountryLabel";
import { Download, ArrowRight } from "lucide-react";
import Header from "@/components/Header";

const SamplePlan = () => {
  const navigate = useNavigate();
  const [isDownloading, setIsDownloading] = useState(false);
  const previewAccordionTitleClass = "font-manrope text-[1.02rem] sm:text-[1.08rem] font-semibold tracking-[-0.01em] text-stone-900";

  const previewCountries = useMemo(
    () => [
      {
        code: "PT",
        name: "Portugal",
        roleLabel: "Top contender",
        roleSummary:
          "In this sample, Portugal leads because it shows how the Blueprint brings settlement ease, housing reality, and long-term livability into one clearer decision.",
      },
      {
        code: "ES",
        name: "Spain",
        roleLabel: "Strong alternative",
        roleSummary:
          "Spain stays close because it shows how the Blueprint keeps a credible alternative in view when one practical question is still unresolved.",
      },
    ],
    []
  );

  const previewProfile = useMemo(
    () => [
      { label: "Budget range", value: "Mid-range monthly budget" },
      { label: "Timeline", value: "Within the next year" },
      { label: "Climate preference", value: "Warmer, day-to-day livability" },
      { label: "Healthcare importance", value: "High confidence required" },
    ],
    []
  );

  const previewPriorities = useMemo(
    () => ["Healthcare confidence", "Monthly cost realism", "Residency practicality", "Lifestyle fit"],
    []
  );

  const previewTradeoffs = useMemo(
    () => [
      {
        title: "Practicality vs systems confidence",
        snippet:
          "Here, the easier move is weighed against the stronger systems case, so you can see whether extra friction is worth it.",
      },
      {
        title: "Narrowing to one country",
        snippet:
          "Here, the decision narrows: one alternative may still deserve comparison, while others stop helping the choice.",
      },
    ],
    []
  );

  const previewTimeline = useMemo(
    () => [
      {
        phase: "Earlier stage — Narrow the choice",
        snippet:
          "You move from broad research into a tighter comparison, so one or two countries begin to deserve serious attention.",
      },
      {
        phase: "Later stage — Prepare around one direction",
        snippet:
          "Once one country continues to hold up, the shortlist can close and preparation can begin around one direction.",
      },
    ],
    []
  );

  const handleDownload = async () => {
    try {
      setIsDownloading(true);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sample-plan/pdf`, {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error("Failed to download sample Blueprint");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "sample-relocation-blueprint.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="sample-plan-page">
      <Header>
        <Button
          data-testid="sample-plan-start-assessment-header"
          onClick={() => navigate("/assessment")}
          className="rounded-full border border-stone-700 bg-stone-900/80 px-4 py-2 text-stone-50 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] transition-[background-color,border-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-amber-400/45 hover:bg-stone-900 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_12px_24px_rgba(0,0,0,0.18)]"
        >
          Start Your Assessment
        </Button>
      </Header>

      <main className="flex-1">
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="mb-8" data-testid="sample-plan-intro">
            <p className="text-sm text-muted-foreground" data-testid="sample-plan-label">
              Sample Relocation Blueprint
            </p>
            <h1 className="font-manrope text-3xl md:text-4xl font-bold text-foreground mb-3" data-testid="sample-plan-title">
              Preview a Relocation Blueprint
            </h1>
            <p className="text-muted-foreground text-base leading-7" data-testid="sample-plan-subtitle">
              See how the Blueprint narrows a broad country search into a clearer decision. It compares your strongest options, tests the tradeoffs, and moves you toward one country you can pursue with confidence.
            </p>
            <div className="mt-5 flex flex-col sm:flex-row gap-3" data-testid="sample-plan-top-actions">
              <Button asChild data-testid="sample-plan-start-assessment-top-btn">
                <Link to="/assessment" className="flex items-center gap-2">
                  Start Your Assessment
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
              <Button
                variant="outline"
                onClick={handleDownload}
                disabled={isDownloading}
                data-testid="download-sample-plan-top-btn"
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                {isDownloading ? "Downloading..." : "Download Sample Blueprint"}
              </Button>
            </div>
          </div>

          <Accordion type="single" collapsible defaultValue="narrowing" className="space-y-4" data-testid="sample-plan-accordion">
            <AccordionItem value="narrowing" className="border border-border/60 rounded-2xl px-4" data-testid="sample-plan-section-narrowing">
              <AccordionTrigger className={previewAccordionTitleClass} data-testid="sample-plan-trigger-narrowing">Where do you currently stand?</AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4 rounded-xl bg-muted/35 p-4 text-sm text-foreground" data-testid="sample-plan-narrowing-content">
                  <div className="grid gap-4 md:grid-cols-3">
                    <div className="rounded-xl border border-border/60 bg-white/80 p-4" data-testid="sample-plan-narrowing-lead-card">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Strongest option right now</p>
                      <div className="font-manrope text-lg text-foreground mb-2">
                        <CountryLabel code="PT" name="Portugal" size={18} flagTestId="sample-plan-narrowing-lead-flag" />
                      </div>
                      <p className="text-sm leading-7 text-muted-foreground">
                        The leading country appears here with the reasoning that puts it ahead, so the choice feels explained rather than asserted.
                      </p>
                    </div>
                    <div className="rounded-xl border border-border/60 bg-white/80 p-4" data-testid="sample-plan-narrowing-alt-card">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Strongest backup option</p>
                      <div className="font-manrope text-lg text-foreground mb-2">
                        <CountryLabel code="ES" name="Spain" size={18} flagTestId="sample-plan-narrowing-alt-flag" />
                      </div>
                      <p className="text-sm leading-7 text-muted-foreground">
                        The strongest alternative stays in view here, so you can see what would need to change before it becomes the better decision.
                      </p>
                    </div>
                    <div className="rounded-xl border border-border/60 bg-white/80 p-4" data-testid="sample-plan-narrowing-tradeoff-card">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">Main deciding tradeoff</p>
                      <p className="text-sm leading-7 text-muted-foreground">
                        The tradeoff most likely to change the answer sits here, so the decision becomes clearer instead of broader.
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-4 md:grid-cols-2">
                    <div className="rounded-xl border border-dashed border-border bg-white/60 p-4" data-testid="sample-plan-narrowing-pressure-point">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">What needs a closer look</p>
                      <p className="text-sm leading-7 text-muted-foreground">
                        The practical question that still needs checking appears here before the front-runner can be trusted.
                      </p>
                    </div>
                    <div className="rounded-xl border border-dashed border-border bg-white/60 p-4" data-testid="sample-plan-narrowing-next-note">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-2">What is most likely to clarify the choice</p>
                      <p className="text-sm leading-7 text-muted-foreground">
                        The next comparison becomes clearer here, because it is the one most likely to reduce uncertainty.
                      </p>
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="profile" className="border border-border/60 rounded-2xl px-4" data-testid="sample-plan-section-profile">
              <AccordionTrigger className={previewAccordionTitleClass} data-testid="sample-plan-trigger-profile">What shaped this Blueprint?</AccordionTrigger>
              <AccordionContent>
                <div className="grid gap-3 sm:grid-cols-2" data-testid="sample-plan-profile-grid">
                  {previewProfile.map((item) => (
                    <div key={item.label} className="rounded-xl border border-border/60 bg-white/70 p-4">
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1">{item.label}</p>
                      <p className="text-sm text-foreground">{item.value}</p>
                    </div>
                  ))}
                </div>
                <p className="mt-4 text-sm leading-7 text-muted-foreground" data-testid="sample-plan-profile-note">
                  These details from your assessment influence which countries remain strong, which tradeoffs matter most, and how your relocation path is evaluated.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="priorities" className="border border-border/60 rounded-2xl px-4" data-testid="sample-plan-section-priorities">
              <AccordionTrigger className={previewAccordionTitleClass} data-testid="sample-plan-trigger-priorities">What mattered most in this Blueprint?</AccordionTrigger>
              <AccordionContent>
                <div className="flex flex-wrap gap-2" data-testid="sample-plan-priorities-chips">
                  {previewPriorities.map((priority) => (
                    <span key={priority} className="inline-flex rounded-full border border-border bg-white/70 px-3 py-1 text-xs font-medium text-stone-700">
                      {priority}
                    </span>
                  ))}
                </div>
                <p className="mt-4 text-sm leading-7 text-muted-foreground" data-testid="sample-plan-priorities-note">
                  Your priorities explain why certain countries rise, why others weaken, and which tradeoffs deserve more attention.
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="countries" className="border border-border/60 rounded-2xl px-4" data-testid="sample-plan-section-countries">
              <AccordionTrigger className={previewAccordionTitleClass} data-testid="sample-plan-trigger-countries">Why is each country still in the mix?</AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4">
                  {previewCountries.map((country) => (
                    <div key={country.code} className="rounded-xl border border-border/60 bg-white/70 p-4" data-testid={`sample-plan-country-${country.code}`}>
                      <div className="flex flex-wrap items-center gap-3 mb-3">
                        <div className="font-manrope text-lg text-foreground">
                          <CountryLabel code={country.code} name={country.name} size={18} flagTestId={`sample-plan-country-flag-${country.code}`} />
                        </div>
                        <span className="inline-flex rounded-full border border-border bg-muted/40 px-3 py-1 text-xs font-medium text-stone-700" data-testid={`sample-plan-country-role-${country.code}`}>
                          {country.roleLabel}
                        </span>
                      </div>
                      <p className="text-sm leading-7 text-muted-foreground" data-testid={`sample-plan-country-summary-${country.code}`}>
                        {country.roleSummary}
                      </p>
                    </div>
                  ))}
                  <div className="rounded-xl border border-dashed border-border bg-muted/30 p-4 text-sm text-muted-foreground" data-testid="sample-plan-country-more-note">
                    By this point, a wider search has narrowed into one or two countries worth pursuing seriously.
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="tradeoffs" className="border border-border/60 rounded-2xl px-4" data-testid="sample-plan-section-tradeoffs">
              <AccordionTrigger className={previewAccordionTitleClass} data-testid="sample-plan-trigger-tradeoffs">What could weaken each option?</AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4">
                  {previewTradeoffs.map((tradeoff) => (
                    <div key={tradeoff.title} className="rounded-xl border border-border/60 bg-white/70 p-4" data-testid={`sample-plan-tradeoff-${tradeoff.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                      <p className="font-medium text-foreground mb-2">{tradeoff.title}</p>
                      <p className="text-sm leading-7 text-muted-foreground">{tradeoff.snippet}</p>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="residency" className="border border-border/60 rounded-2xl px-4" data-testid="sample-plan-section-residency">
              <AccordionTrigger className={previewAccordionTitleClass} data-testid="sample-plan-trigger-residency">How realistic is the residency path?</AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4">
                  <div className="rounded-xl border border-border/60 bg-white/70 p-4" data-testid="sample-plan-residency-snippet">
                    <p className="text-sm leading-7 text-muted-foreground">
                      Residency realities test whether a country still works once paperwork, timing, and likely pathways are considered in real terms.
                    </p>
                  </div>
                  <div className="rounded-xl border border-dashed border-border bg-muted/30 p-4 text-sm text-muted-foreground" data-testid="sample-plan-residency-note">
                    A country that looks appealing in theory either becomes realistic here—or falls away before you invest more energy.
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="timeline" className="border border-border/60 rounded-2xl px-4" data-testid="sample-plan-section-timeline">
              <AccordionTrigger className={previewAccordionTitleClass} data-testid="sample-plan-trigger-timeline">In what order should you work through this?</AccordionTrigger>
              <AccordionContent>
                <div className="space-y-4">
                  {previewTimeline.map((phase, index) => (
                    <div key={phase.phase} className="rounded-xl border border-border/60 bg-white/70 p-4" data-testid={`sample-plan-timeline-${index + 1}`}>
                      <p className="font-medium text-foreground mb-2">{phase.phase}</p>
                      <p className="text-sm leading-7 text-muted-foreground">{phase.snippet}</p>
                    </div>
                  ))}
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <div className="hover-contrast-surface mt-10 rounded-2xl border border-border/60 bg-muted/30 p-6 text-center" data-testid="sample-plan-cta-box">
            <h2 className="hover-contrast-text font-manrope text-2xl font-semibold text-foreground mb-3" data-testid="sample-plan-cta-title">
              Build Your Relocation Blueprint
            </h2>
            <p className="hover-contrast-text-muted text-sm text-muted-foreground mb-6" data-testid="sample-plan-cta-subtitle">
              Start your assessment to compare your strongest options, understand the tradeoffs, and narrow toward one country you can pursue with confidence.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <Button asChild data-testid="sample-plan-start-assessment-cta" className="hover-contrast-button-solid">
                <Link to="/assessment" className="flex items-center gap-2">
                  Start Your Assessment
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
              <Button
                variant="outline"
                onClick={handleDownload}
                disabled={isDownloading}
                data-testid="download-sample-plan-cta-btn"
                className="hover-contrast-button-outline flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                {isDownloading ? "Downloading..." : "Download Sample Blueprint"}
              </Button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default SamplePlan;